import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, map, Observable } from 'rxjs';
import { BaseApiService } from '@core/api/services/base-api.service';
import { ApiServiceName } from '@core/api/enums/controllers';
import { ManufactoryControllerEnum } from '@core/api/enums/controllers/manufactory-controller.enum';
import { ApiService } from '@core/api/services/api.service';
import { DailyPlanDetailModel, OperationStateModel } from '@app/features/manufactory/models/daily-plans.model';
import { SnackbarService } from '@core/services/snackbar.service';

export enum ManufacturingOperationState {
  Ready = '1',
  Start = '3',
  Pause = '4',
  Return = '5',
  Continue = '6',
  Stop = '8',
}

@Injectable({
  providedIn: 'root',
})
export class DailyPlanStateService extends BaseApiService<ApiServiceName.Manufactory, ManufactoryControllerEnum.DailyPlans> {
  protected override readonly _apiService: ApiService;
  private readonly _httpClient = inject(HttpClient);
  private readonly _snackbarService = inject(SnackbarService);

  constructor() {
    const _apiService = inject(ApiService);

    super(_apiService, ApiServiceName.Manufactory, ManufactoryControllerEnum.DailyPlans);

    this._apiService = _apiService;
  }

  getCurrentOperationState(
    operationId: string,
    param?: unknown,
  ): Observable<{ operation: DailyPlanDetailModel['operation']; param: unknown }> {
    return this._httpClient
      .get<DailyPlanDetailModel>(`${this.baseUrl}/${operationId}`)
      .pipe(map(dailyPlan => ({ operation: dailyPlan.operation, param })));
  }

  /**
   * Tests whether the backend offered `target` in this state's `availableStateCodes`.
   *
   * A plain comparison is correct again. Until 2026-08-21 this stripped characters that cannot appear in a
   * state code, because state `4`'s entry for `8` was stored as `8"` and `AvailableStateCodes` reaches the
   * frontend raw. The backend repaired the lookup (migration `1.42.0`, data-driven: an entry is rewritten only
   * when it matches no known `Code` yet matches one with quotes stripped, so a legitimate code such as `-1`
   * cannot be touched), and the tolerance was removed with it.
   */
  private offersTransitionTo(state: OperationStateModel, target: ManufacturingOperationState): boolean {
    return (state.availableStateCodes ?? []).includes(target);
  }

  getNextOperationState(state: OperationStateModel | undefined, trigger: 'start' | 'continue' | 'pause' | 'stop' | 'return') {
    if (!state) return '';
    if (trigger === 'start') {
      if (state.code === ManufacturingOperationState.Ready && this.offersTransitionTo(state, ManufacturingOperationState.Start)) {
        return ManufacturingOperationState.Start;
      }
      if (state.code === ManufacturingOperationState.Pause && this.offersTransitionTo(state, ManufacturingOperationState.Continue)) {
        return ManufacturingOperationState.Continue;
      }
    }
    if (trigger === 'continue') {
      if (state.code === ManufacturingOperationState.Pause && this.offersTransitionTo(state, ManufacturingOperationState.Continue)) {
        return ManufacturingOperationState.Continue;
      }
    }
    if (trigger === 'pause') {
      if (
        (state.code === ManufacturingOperationState.Start || state.code === ManufacturingOperationState.Continue) &&
        this.offersTransitionTo(state, ManufacturingOperationState.Pause)
      ) {
        return ManufacturingOperationState.Pause;
      }
    }
    if (trigger === 'stop') {
      // ▶ Pause (`4`) belongs here, because the backend's state machine allows it:
      // `OperationState.PartiallyFinished` declares transitions to `6, 7C, 8, 9C`, and the stored lookup
      // confirms it. Until 2026-08-20 this branch omitted `4`, so pressing Finish on a paused operation
      // resolved to '' and sent an empty `stateCode`.
      //
      // 🔴 The original note here justified that by "the UI has always offered Finish from a paused
      // operation (`canFinish` accepts `['3','4','6']`)". That is wrong and was corrected on 2026-08-28
      // during the manual window. `canFinish` does accept `4`, but only the MECHANICAL controls read it;
      // the hand-operated Finish button in `operation-work.component.html` is gated on
      // `manualWorkLoggingInProgress()` - true only in states `3`/`6` (derived on every reload) - so on that path
      // Finish is unreachable while paused, and pressing Start first moves the operation to `6`. Accepting
      // `4` here is still correct: the transition is real, this branch is shared with every other caller,
      // and a resolver that refuses a transition the backend allows is the bug it was fixing.
      if (
        (state.code === ManufacturingOperationState.Ready ||
          state.code === ManufacturingOperationState.Start ||
          state.code === ManufacturingOperationState.Pause ||
          state.code === ManufacturingOperationState.Continue) &&
        this.offersTransitionTo(state, ManufacturingOperationState.Stop)
      ) {
        return ManufacturingOperationState.Stop;
      }
    }
    if (trigger === 'return') {
      if (state.code === ManufacturingOperationState.Ready && this.offersTransitionTo(state, ManufacturingOperationState.Return)) {
        return ManufacturingOperationState.Return;
      }
    }
    return '';
  }

  /**
   * Moves an operation to `stateCode`.
   *
   * ▶ **An empty `stateCode` is never sent.** `getNextOperationState` returns `''` when it cannot resolve a
   * transition, and every call site feeds its result straight in here. `stateCode` is mandatory on the API
   * (`SetOperationStateRequestValidator` declares it `CustomNotEmptyAsRequired`), so sending `''` produced a
   * 400 - and since these are buttons rather than a form, there was no control for that validation message to
   * land on, so the user got an error or nothing at all.
   *
   * After the `stop` alignment above this should be unreachable for any transition the UI offers, so reaching
   * it means the state machine and the UI have drifted apart again. It is **reported and then stopped**: the
   * message is shown here and the observable completes without emitting, so no caller runs its success path
   * and none of them report a second time. A silent no-op would be worse than the error it replaces; two
   * messages for one cause would be worse than either.
   */
  updateOperationState(id: string, stateCode: string) {
    if (!stateCode) {
      console.error(`No operation state transition resolved for operation '${id}'; the request was not sent.`);
      this._snackbarService.addSnackbarAndTranslateIt({ message: 'common.entity.unexpectedError', iconType: 'error' });

      return EMPTY;
    }

    const operationUrl = this._apiService.getControllerApiUrl(ApiServiceName.Manufactory, ManufactoryControllerEnum.Operations);

    return this._httpClient.put<any>(`${operationUrl}/${id}/state`, { stateCode });
  }
}
